import { useEffect } from "react";
import { CheckCircle2, Sparkles, X } from "lucide-react";

/**
 * Celebratory modal that pops when a recruit "approves" a pitch in
 * Practice Mode. No real API call fires — this is the success signal.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - businessName: string  (echoed in the copy)
 */
export default function PracticeSuccessModal({ open, onClose, businessName }) {
  // ESC closes.
  useEffect(() => {
    if (!open) return;
    const h = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="practice-success-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: "rgba(4,7,12,0.75)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      {/* Sparkle burst layer */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(18)].map((_, i) => (
          <span
            key={`spark-${i}`}
            className="absolute rounded-full"
            style={{
              left: `${(i * 53) % 100}%`,
              top: `${(i * 37) % 100}%`,
              width: 6,
              height: 6,
              background: i % 2 === 0 ? "#D4AF37" : "#34D399",
              opacity: 0,
              animation: `practice-spark 1400ms ${i * 60}ms ease-out forwards`,
              boxShadow: "0 0 12px currentColor",
              color: i % 2 === 0 ? "#D4AF37" : "#34D399",
            }}
          />
        ))}
      </div>
      <style>{`
        @keyframes practice-spark {
          0%   { opacity: 0; transform: scale(0.4) translateY(0); }
          20%  { opacity: 1; transform: scale(1.1) translateY(-4px); }
          100% { opacity: 0; transform: scale(0.6) translateY(-40px); }
        }
        @keyframes practice-badge-pop {
          0%   { transform: scale(0.6); opacity: 0; }
          70%  { transform: scale(1.05); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>

      <div
        role="document"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-sm border overflow-hidden"
        style={{
          borderColor: "rgba(212,175,55,0.6)",
          background:
            "linear-gradient(160deg, rgba(6,11,20,0.98) 0%, rgba(16,185,129,0.08) 60%, rgba(212,175,55,0.14) 100%)",
          boxShadow: "0 30px 80px -10px rgba(212,175,55,0.35)",
        }}
      >
        {/* Close */}
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          data-testid="practice-success-close"
          className="absolute top-3 right-3 w-8 h-8 rounded-sm flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10"
        >
          <X size={14} />
        </button>

        <div className="p-6 sm:p-8 text-center">
          {/* Badge */}
          <div
            className="mx-auto w-16 h-16 rounded-full border flex items-center justify-center mb-4"
            style={{
              borderColor: "rgba(212,175,55,0.7)",
              background: "rgba(212,175,55,0.15)",
              color: "#D4AF37",
              boxShadow: "0 0 30px rgba(212,175,55,0.45)",
              animation: "practice-badge-pop 500ms ease-out",
            }}
          >
            <CheckCircle2 size={30} />
          </div>

          <div className="text-[10px] uppercase tracking-[0.32em] font-mono-aviation text-emerald-300 mb-2 flex items-center justify-center gap-2">
            <Sparkles size={11} /> Simulator · Training Mode
          </div>
          <h3 className="font-display text-2xl text-white leading-tight">
            Success — <span className="text-gold italic">nice work</span>, operator.
          </h3>
          <p className="mt-3 text-sm text-slate-300 leading-relaxed">
            In live mode, this pitch{" "}
            {businessName ? (
              <>
                for <span className="text-gold">{businessName}</span>{" "}
              </>
            ) : null}
            would now be queued in the GVO Auto-Net pipeline. No live message
            has been sent.
          </p>

          <div className="mt-6 flex items-center justify-center">
            <button
              type="button"
              onClick={onClose}
              data-testid="practice-success-continue"
              className="btn-gold px-6 py-3 rounded-sm text-[11px] uppercase tracking-[0.24em] flex items-center gap-2 min-h-[44px]"
            >
              Continue Training
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
